import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from 'apollo-react/components/Grid';
import Typography from '../../../common/StyledTypography';
import CommonModal from '../../../common/CommonModal';
import DraftCard from './DraftCard';
import FiltersPanel from '../../../common/Reviews_Cards/FiltersPanel';
import { matchesDateFilter } from '../../../common/Reviews_Cards/tabUtils';
import useDraftsApi from '../../../../hooks/api/drafts/useDraftsApi';
import '../../../common/Reviews_Cards/styles.css';

/**
 * DraftsList - Component for displaying a list of draft requests
 * Uses the same layout as ReviewsListCards for consistency
 */
const DraftsList = ({ onDraftDeleted }) => {
  const { t } = useTranslation();
  const { getUserDrafts, deleteDraft, loading } = useDraftsApi();
  const [drafts, setDrafts] = useState([]);
  const [filteredDrafts, setFilteredDrafts] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [filters, setFilters] = useState({
    referenceOrStudyName: '',
    dateRange: null,
  });

  // Fetch drafts on mount
  const fetchDrafts = useCallback(async () => {
    try {
      const fetchedDrafts = await getUserDrafts();
      setDrafts(fetchedDrafts || []);
      setFilteredDrafts(fetchedDrafts || []);
    } catch (error) {
      console.error('Error fetching drafts:', error);
      setDrafts([]);
      setFilteredDrafts([]);
    }
  }, [getUserDrafts]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  // Handle filter input changes
  const handleFilterChange = (field, value) => {
    try {
      setFilters((prev) => ({
        ...prev,
        [field]: value,
      }));
    } catch (error) {
      console.error('Error updating filter:', error);
    }
  };

  // Apply filters when button is clicked
  const handleApplyFilters = () => {
    try {
      if (!drafts) return;

      const filtered = drafts.filter((draft) => {
        // Search in both generated_id (from request) and study_name
        const searchTerm = filters.referenceOrStudyName.toLowerCase();
        const referenceOrStudyNameMatch =
          !searchTerm ||
          draft.request?.generated_id?.toLowerCase().includes(searchTerm) ||
          draft.study_name?.toLowerCase().includes(searchTerm);

        // Date range filter - use drafted_at as the date field
        const dateMatch = matchesDateFilter(
          { createdAt: draft.draftedAt || draft.drafted_at },
          filters.dateRange
        );

        return referenceOrStudyNameMatch && dateMatch;
      });

      setFilteredDrafts(filtered);
    } catch (error) {
      console.error('Error applying filters:', error);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = useCallback((draftId) => {
    try {
      setDraftToDelete(draftId);
      setDeleteModalOpen(true);
    } catch (error) {
      console.error('Error opening delete modal:', error);
    }
  }, []);

  // Handle delete confirmation
  const handleConfirmDelete = useCallback(async () => {
    try {
      if (draftToDelete) {
        const success = await deleteDraft(draftToDelete);
        if (success) {
          setDrafts((prev) => prev.filter((d) => d.id !== draftToDelete));
          setFilteredDrafts((prev) =>
            prev.filter((d) => d.id !== draftToDelete)
          );
          onDraftDeleted?.();
        }
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
    } finally {
      setDeleteModalOpen(false);
      setDraftToDelete(null);
    }
  }, [draftToDelete, deleteDraft, onDraftDeleted]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    try {
      setDeleteModalOpen(false);
      setDraftToDelete(null);
    } catch (error) {
      console.error('Error canceling delete:', error);
    }
  }, []);

  if (loading && drafts.length === 0) {
    return (
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <FiltersPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onApplyFilters={handleApplyFilters}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '40px',
            }}
          >
            <Typography variant="bodyDefault">
              {t('Loading drafts...')}
            </Typography>
          </div>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={2} sx={{ width: '100%' }}>
        {/* Filter Section - same as other tabs */}
        <Grid size={{ xs: 12, md: 3 }}>
          <FiltersPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onApplyFilters={handleApplyFilters}
          />
        </Grid>

        {/* Drafts List - same layout as other tabs */}
        <Grid size={{ xs: 12, md: 9 }}>
          {filteredDrafts.length === 0 ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '40px',
              }}
            >
              <Typography variant="bodyDefault">
                {t(
                  'No drafts found. Start a new request and save it as draft.'
                )}
              </Typography>
            </div>
          ) : (
            filteredDrafts.map((draft) => (
              <DraftCard
                key={draft.id}
                draft={draft}
                onDelete={handleDeleteClick}
              />
            ))
          )}
        </Grid>
      </Grid>

      <CommonModal
        open={deleteModalOpen}
        onClose={handleCancelDelete}
        title={t('Delete Draft')}
        message={t(
          'Are you sure you want to delete this draft? This action cannot be undone.'
        )}
        buttonProps={[
          {
            label: t('Cancel'),
            onClick: handleCancelDelete,
          },
          {
            label: t('Delete'),
            onClick: handleConfirmDelete,
            color: 'secondary',
          },
        ]}
      />
    </>
  );
};

export default DraftsList;
